import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { TextAreaRef } from 'antd/lib/input/TextArea';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import Space from 'antd/lib/space';
import { BroadcastEvent, EnvironmentInfo, KemuHubFunctions, SerializableServiceInfo } from '@kemu-io/hs-types';
import byteSize from 'byte-size';
import { useDebouncedCallback } from 'use-debounce';
import { ReactComponent as InstallIcon } from '../../assets/install-icon.svg';
import styles from './ServiceInstallerModal.module.css';
import connectionManager from '@src/app/kemuHub/connectionManager';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';
import useCommonWords from '@hooks/useCommonWords';
import FormGroup from '@components/form-control/formGroup/formGroup';
import StyledLabel from '@components/form-control/styledLabel/styledLabel';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import useHandleServiceBroadcast from '@hooks/useHandleServiceBroadcast';
import FileDropZone from '@components/FileDropZone/FileDropZone';
import StyledTextArea from '@components/form-control/styledTextArea/styledTextArea';
import { InstallServiceConfig } from '@src/types/hubService';
import { useHubContext } from '@common/context/HubContext/HubContext';

type Props = {
  visible: boolean;
  onClose: () => void;
}

const ServiceInstallerName = 'kemu.io.serviceInstaller';
const InstallServiceEvent = 'installService';

const getDefaultConfig = (): InstallServiceConfig => ({
  keepPreviousVersion: false,
  replaceCurrentVersion: false,
  source: {
    zip: null,
  },
});

/**
 * Formats bytes into a compact human-readable string.
 * @returns the formatted size string
 */
const getHumanSize = (bytes: number): string => {
  const size = byteSize(bytes || 0);
  return `${size.value} ${size.unit}`;
};

/**
 * Helper function to find the service installer in the list of active services.
 * @param services a list of active services
 * @returns a service info object if the installer service is found, otherwise null.
 */
export const findServiceInstaller = (services: SerializableServiceInfo[]): SerializableServiceInfo | null => {
  const serviceInstaller  = services.find((service) => service.name === ServiceInstallerName && service.internal);
  return serviceInstaller || null;
};

/**
 * Modal that guides users through installing a Hub Service zip file.
 * @returns the rendered modal
 */
const ServiceInstallerModal = (props: Props): React.JSX.Element => {
  const { visible, onClose } = props;
  const t = useTranslation('Modal.HubService.InstallService');
  const cw = useCommonWords();
  const hubConnector = connectionManager.getConnector();
  const hubOnline = connectionManager.isReady();
  const [zipData, setZipData] = useState<Uint8Array | null>(null);
  const [config, setConfig] = useState<InstallServiceConfig>(getDefaultConfig());
  const [logs, setLogs] = useState<string>('');
  const [installationInProgress, setInstallationInProgress] = useState<boolean>(false);
  const [missingSecrets, setMissingSecrets] = useState<boolean>(false);
  const [processSuccessful, setProcessSuccessful] = useState<boolean>(false);
  const logsInputRef = useRef<TextAreaRef>(null);
  const autoScrollSetAt = useRef<number>(0);
  const lastScrollAt = useRef<number>(0);
  const { services } = useHubContext();
  const serviceInstaller  = findServiceInstaller(services.available);
  const [installedService, setInstalledService] = useState<SerializableServiceInfo | null>(null);

  const scrollToBottom = useDebouncedCallback(() => {
    if (logsInputRef.current?.resizableTextArea?.textArea) {
      const textAreaComponent = logsInputRef.current.resizableTextArea.textArea;
      autoScrollSetAt.current = Date.now();
      textAreaComponent.scrollTop = textAreaComponent.scrollHeight;
    }
  }, 100);

  useHandleServiceBroadcast<BroadcastEvent>(serviceInstaller?.name, serviceInstaller?.version, (event) => {
    if (event.source.serviceName === ServiceInstallerName) {
      const stdout = event.outputs.find(output => output.name === 'stdout');
      if (stdout) {
        let firstLogLine = false;
        setLogs((l) => {
          firstLogLine = l.split('\n').length <= 2;
          return `${l}${stdout.value}\n`;
        });

        const elapsed = Date.now() - lastScrollAt.current;

        // If the user hasn't scrolled in the last 15 seconds, scroll to the bottom
        if (elapsed > 15000 || firstLogLine) {
          scrollToBottom();
        }
      }
    }
  }, 'app', !visible);

  const handleClose = useCallback(() => {
    onClose();
    setConfig(getDefaultConfig());
    setInstallationInProgress(false);
    setProcessSuccessful(false);
    setZipData(null);
    setLogs('');
    setMissingSecrets(false);
  }, [onClose]);

  const handleKeepPrevVerToggle = useCallback((checked: boolean) => {
    setConfig((prev) => ({
      ...prev,
      keepPreviousVersion: checked,
    }));
  }, []);

  const handleOpenKemuHubSettings = async () => {
    if (!installedService) { return; }

    hubConnector.executeHubFunction(KemuHubFunctions.ShowSecretsConfigScreen, [{
      name: installedService?.name,
      version: installedService?.version,
    }]).catch(res => {
      message.error(
        res.errCode === 'GUI_NOT_SUPPORTED' ? t.withBaseKey('Error')('GuiNotSupported') : res.error || t('DefaultError')
      );
    });
  };

  const handleReplaceCurrentVerToggle = useCallback((checked: boolean) => {
    setConfig((prev) => ({
      ...prev,
      replaceCurrentVersion: checked,
    }));
  }, []);

  const handleZipAdded = useCallback((data: Uint8Array) => {
    setZipData(data);
    setLogs('');
  }, []);

  const handleLogsScroll = useCallback(() => {
    const isAutoScroll = Date.now() - autoScrollSetAt.current < 200;
    if (!isAutoScroll) {
      lastScrollAt.current = Date.now();
    }
  }, []);

  const handleInstall = useCallback(async () => {
    setInstallationInProgress(true);
    setLogs('Uploading service...\n');

    try {
      if (!serviceInstaller) { return; }

      const request: InstallServiceConfig = {
        ...config,
        source: {
          zip: (zipData?.buffer as ArrayBuffer) || null,
        }
      };

      const response = await hubConnector.callProcessorHandler(
        serviceInstaller.sessionId,
        {} as EnvironmentInfo,
        InstallServiceEvent,
        request,
        {
          timeout: 900000,
        }
      );

      console.log('Service install response:', response);
      if (response.success) {
        setProcessSuccessful(true);
        setInstalledService(response.success as SerializableServiceInfo);
      }

      if (response.launchedWithErrors) {
        console.log('Service install error:', response.launchedWithErrors);

        if (response.launchedWithErrors.errorCode === 'MISSING_REQUIRED_SECRETS') {
          setMissingSecrets(true);
        }

        await connectionManager.refreshServicesCache();
      }
    } catch (e) {
      console.error('Service install error:', e);
      setLogs((l) => `${l}${e.message || e as string}\n`);
    }

    setInstallationInProgress(false);
    scrollToBottom();
  }, [hubConnector, config, scrollToBottom, serviceInstaller, zipData?.buffer]);

  useEffect(() => {
    if (visible) {
      setLogs('');
      setMissingSecrets(false);
      setProcessSuccessful(false);
      setInstallationInProgress(false);
      setInstalledService(null);
      setZipData(null);
      setConfig(getDefaultConfig());
    }
  }, [visible]);

  return (
    <RoundedModal
      title={t('Title', 'Install Service')}
      visible={visible}
      onCancel={handleClose}
      closeOnMaskClick={false}
      closable={true}
      disableOkButton={
        processSuccessful
        || !hubOnline
        || installationInProgress
        || !zipData
      }
      disableCancelButton={installationInProgress}
      loadingOkBtn={installationInProgress}
      okBtnLabel={cw('Install')}
      cancelBtnLabel={processSuccessful ? cw('Close') : cw('Cancel')}
      onOk={handleInstall}
    >

      <FormGroup marginBottomLevel={3}>
        <Space.Compact style={{ width: '100%' }}>
          <FileDropZone
            accept='.zip'
            className={styles.DropZone}
            hoverClass={styles.DropHover}
            noFileElement={
              <div className={styles.CenteredContent}>{t('Description')}</div>
            }
            fileLoaded={!!zipData}
            onFileAdded={handleZipAdded}
            yesFileElement={
              <div className={styles.CenteredContent}>
                <InstallIcon className={styles.InstallIcon} />
                {zipData && getHumanSize(zipData.byteLength)}
              </div>
            }
          />
        </Space.Compact>
      </FormGroup>

      <FormGroup vCenter marginBottomLevel={2} row className={styles.SwitchGroup}>
        <StyledLabel text={t('KeepPreviousVersion')} helpText={t('KeepPreviousVersion.Help')} />
        <KemuSwitch
          disabled={!serviceInstaller || installationInProgress}
          checked={!!config.keepPreviousVersion}
          onChange={handleKeepPrevVerToggle}
          size='small'
        />
      </FormGroup>

      <FormGroup vCenter marginBottomLevel={2} row className={styles.SwitchGroup}>
        <StyledLabel
          text={t('ReplaceCurrentVersion')}
          helpText={t('ReplaceCurrentVersion.Help')}
        />
        <KemuSwitch
          disabled={!serviceInstaller || installationInProgress}
          checked={!!config.replaceCurrentVersion}
          onChange={handleReplaceCurrentVerToggle}
          size='small'
        />
      </FormGroup>

      {!!logs.length && (
        <div className={styles.LogsContainer}>
          {!hubOnline ? (
            <div>
              <Alert
                message={t('HubOffline', 'Kemu Hub is currently offline')}
                type="error"
                showIcon
              />
            </div>
          ) : (
            <>
              <StyledTextArea
                ref={logsInputRef}
                onScroll={handleLogsScroll}
                className={styles.Logs}
                readOnly
                value={logs}
                label={t('Logs.Label', 'Logs')}
                rows={6}
                preventResize
              />

              {missingSecrets && (
                <Alert message={t('MissingSecrets', undefined, {
                  link: <Button
                    type='link'
                    onClick={handleOpenKemuHubSettings}
                    className={styles.ButtonLink}
                  >
                    {t.withBaseKey('CommonWords')('KemuHubSettings')}
                  </Button>
                })} type="warning" showIcon style={{ marginTop: 10 }} />
              )}
            </>
          )}
        </div>
      )}
    </RoundedModal>
  );
};

export default ServiceInstallerModal;


